// components/EvaluationDetailModal.jsx
import React from 'react';
import { Modal, Badge, Button } from 'react-bootstrap';

const fmtPct = (v, dp = 0) =>
  (typeof v === 'number' ? `${(v * 100).toFixed(dp)}%` : '—');

const fmtNum = (v, dp = 2) =>
  (typeof v === 'number' && !Number.isNaN(v) ? Number(v).toFixed(dp) : '—');

const fmtTime = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  } catch {
    return '—';
  }
};

const Section = ({ title, children, pad = true }) => (
  <div className="mb-3 rounded" style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
    <div className="px-3 pt-3">
      <h6 className="mb-2" style={{ color: '#9aa4b2', letterSpacing: 0.2 }}>{title}</h6>
    </div>
    <div className={pad ? 'px-3 pb-3' : ''}>
      {children}
    </div>
  </div>
);

const KeyVal = ({ label, value, strong = true }) => (
  <div className="col-12 col-sm-6 col-md-3">
    <small className="d-block" style={{ color: '#888' }}>{label}</small>
    {strong ? (
      <strong style={{ color: '#e0e0e0' }}>{value}</strong>
    ) : (
      <span style={{ color: '#e0e0e0' }}>{value}</span>
    )}
  </div>
);

// Aug helpers
const isNonEmptyObject = (o) => o && typeof o === 'object' && Object.keys(o).length > 0;

const JsonBlock = ({ data }) => (
  <pre
    className="m-0 p-2 rounded"
    style={{ backgroundColor: '#0f131b', border: '1px solid #1e2535', color: '#cfd6e4', whiteSpace: 'pre-wrap' }}
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);

const OptionalSection = ({ title, value }) => {
  if (value == null) return null;
  const t = typeof value;
  if (t === 'string' && value.trim() === '') return null;
  return (
    <Section title={title}>
      {t === 'string' ? (
        <div className="p-2 rounded" style={{ background: '#12151d', border: '1px solid #232a3a', whiteSpace: 'pre-wrap' }}>
          {value}
        </div>
      ) : (
        <JsonBlock data={value} />
      )}
    </Section>
  );
};

// === Chart LLM helpers & component ===
const buildChartLLM = (chartAnalysis) => {
  if (!chartAnalysis) return null;
  const llm = chartAnalysis.llm_full_response || {};
  const oa = llm.overall_assessment || {};
  const fft = llm.fft_lag_intelligence || {};
  const patterns = Array.isArray(llm.identified_patterns) ? llm.identified_patterns : [];

  return {
    dominant_direction: oa.dominant_direction ?? 'neutral',
    action_recommendation: oa.action_recommendation ?? 'hold_position',
    confidence_in_analysis: oa.confidence_in_analysis ?? null,
    pattern_confluence_strength: oa.pattern_confluence_strength ?? null,
    bullish_signal_strength: oa.bullish_signal_strength ?? null,
    bearish_signal_strength: oa.bearish_signal_strength ?? null,
    support: oa.key_support_level ?? null,
    resistance: oa.key_resistance_level ?? null,
    conflicting_signals_present: !!oa.conflicting_signals_present,
    patterns: patterns.slice(0, 3).map((p) => ({
      name: p.pattern_name,
      type: p.pattern_type,
      direction:
        p.interpretation?.toLowerCase().includes('bear') ? 'bearish' :
        p.interpretation?.toLowerCase().includes('bull') ? 'bullish' : 'neutral',
      confidence: p.confidence ?? 0,
      rec: p.recommended_action ?? null,
    })),
    timing_summary: fft.timing_relationships_summary ?? null,
    optimal_action_timing: fft.optimal_action_timing ?? null,
    lag_confidence_score: fft.lag_confidence_score ?? null,
    raw: llm,
    source_node: chartAnalysis?.analyzed_at_node ?? null,
  };
};

const ChartLLMAnalysis = ({ data }) => {
  if (!data) return null;
  const pct = (v) => (v === 0 || v ? `${Math.round(v * 100)}%` : '—');
  const dirBadge = (dir) => (dir === 'bullish' ? 'success' : dir === 'bearish' ? 'danger' : 'secondary');

  return (
    <div className="p-3 rounded" style={{ backgroundColor: '#12151d', border: '1px solid #232a3a' }}>
      {/* Quick context row */}
      <div className="mb-2 d-flex flex-wrap gap-2">
        <Badge bg={dirBadge(data.dominant_direction)}>🧭 Direction: {data.dominant_direction}</Badge>
        <Badge bg="light" text="dark">🎯 Action: {data.action_recommendation}</Badge>
        {data.confidence_in_analysis != null && (
          <Badge bg="light" text="dark">🔑 Conf: {data.confidence_in_analysis.toFixed(2)}</Badge>
        )}
        {data.pattern_confluence_strength != null && (
          <Badge bg="light" text="dark">🧩 Confluence: {pct(data.pattern_confluence_strength)}</Badge>
        )}
        {data.support && <Badge bg="light" text="dark">🛡️ S: {data.support}</Badge>}
        {data.resistance && <Badge bg="light" text="dark">🧱 R: {data.resistance}</Badge>}
        {data.conflicting_signals_present && <Badge bg="warning" text="dark">⏱️ Conflicting</Badge>}
      </div>

      {/* Patterns */}
      {data.patterns?.length > 0 && (
        <>
          <small className="d-block mb-1" style={{ color: '#9aa4b2' }}>🧠 Top Patterns</small>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {data.patterns.map((p, i) => (
              <Badge key={i} bg={dirBadge(p.direction)}>
                {p.name} • {p.type} • {pct(p.confidence)}
              </Badge>
            ))}
          </div>
        </>
      )}

      {/* Timing */}
      {(data.timing_summary || data.optimal_action_timing || data.lag_confidence_score != null) && (
        <div className="mb-1" style={{ color: '#cfd6e4' }}>
          {data.timing_summary && <small className="d-block">⏱️ Timing: {data.timing_summary}</small>}
          {data.optimal_action_timing && <small className="d-block">📌 Next trigger: {data.optimal_action_timing}</small>}
          {data.lag_confidence_score != null && (
            <small className="d-block">🔭 Lag confidence: {data.lag_confidence_score.toFixed(2)}</small>
          )}
        </div>
      )}
      {data.source_node && (
        <small className="text-muted d-block mt-1">🔹 Analyzed at: {data.source_node}</small>
      )}

      {/* Collapsible raw payload (kept simple) */}
      <details className="mt-2">
        <summary style={{ cursor: 'pointer', color: '#9aa4b2' }}>Raw chart LLM JSON</summary>
        <div className="mt-2">
          <JsonBlock data={data.raw} />
        </div>
      </details>
    </div>
  );
};

const EvaluationDetailModal = ({ evaluation, show, onHide }) => {
  if (!evaluation) return null;

  // Normalize top-level fields (back-compat)
  const fullData = evaluation.fullData || evaluation || {};
  const agentStory =
    fullData.agent_story ||
    fullData.eval_agent_story ||
    evaluation.agent_story ||
    {};

  const chartLLM = buildChartLLM(agentStory.chart_analysis);

  // Shorthands for new schema
  const ids = agentStory.ids || {};
  const dc = agentStory.decision_context || {};
  const ds = agentStory.decision || {};
  const perf = agentStory.performance || {};
  const msum = agentStory.memory_summary || {};
  const ptag = agentStory.pattern_tag_analysis || {};
  const think = agentStory.thinking_snapshots || {};
  const rctl = agentStory.risk_controls || {};
  const contra = agentStory.contradictions || {};
  const ops = agentStory.ops || agentStory.stats || {};
  const narrative = fullData.narrative_summary || evaluation.narrative_summary;

  // Legacy fallbacks and extra fields
  const legacyReasoning = fullData.reasoning || evaluation.reasoning;
  const patternInfluence = fullData.pattern_influence;
  const executionTime = fullData.execution_time; // seconds
  const timeframe = agentStory.timeframe_protection || {};
  const genAIraw = agentStory; // raw llm content if you kept it

  // Legacy/experimental blocks (render if present)
  const exitRisk = fullData.exit_risk || agentStory.exit_risk;
  const redFlags = fullData.red_flags || agentStory.red_flags;
  const journey = fullData.journey || agentStory.journey;
  const signals = fullData.signals || agentStory.signals;

  const finalDecisionChip = ds.final || evaluation.decision || fullData.decision;

  const extractDecisionTime = (positionId) => {
    if (!positionId || typeof positionId !== 'string') return null;
    const parts = positionId.split('-');
    const last = parts[parts.length - 1];
    if (!/^\d{6}$/.test(last)) return null; // expect HHMMSS
    const hh = last.slice(0, 2);
    const mm = last.slice(2, 4);
    const ss = last.slice(4, 6);
    return `${hh}:${mm}:${ss}`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      contentClassName="bg-dark text-light"
    >
      <div style={{ backgroundColor: '#0b0d12' }}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ backgroundColor: '#10131a', borderBottom: '1px solid #1e2330' }}
        >
          <Modal.Title>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>🤖 AI Evaluation Details</span>
              {finalDecisionChip && (
                <Badge bg="secondary" style={{ backgroundColor: '#2b3342', border: '1px solid #3b4352' }}>
                  {String(finalDecisionChip).toUpperCase()}
                </Badge>
              )}
              {perf?.confidence_level && (
                <Badge bg="secondary" style={{ backgroundColor: '#273145', border: '1px solid #38445a' }}>
                  Conf: {perf.confidence_level}
                </Badge>
              )}
              {perf?.similarity_score !== undefined && (
                <Badge bg="secondary" style={{ backgroundColor: '#20333b', border: '1px solid #2e4d57' }}>
                  Sim: {fmtPct(perf.similarity_score)}
                </Badge>
              )}
              {typeof executionTime === 'number' && (
                <Badge bg="secondary" style={{ backgroundColor: '#1e2a2f', border: '1px solid #2c3f45' }}>
                  Exec: {executionTime.toFixed(2)}s
                </Badge>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#9aa4b2' }}>
              {ids?.position_id ? `Position: ${ids.position_id}` : evaluation.position_id || ''}
              {agentStory?.schema_version ? ` • Schema ${agentStory.schema_version}` : ''}
  
              <small className="text-muted ms-2">🕙 {agentStory.analysis_time}</small>

            </div>
            {agentStory?.ids?.cluster_id && (
              <small className="text-muted ms-2">Cluster: {agentStory.ids.cluster_id}</small>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: 'linear-gradient(180deg, #0b0d12, #0a0b0e)', padding: '18px' }}>
          {/* Decision & Context */}
          {(ds.final || dc.symbol || dc.side || dc.price_at_decision !== undefined) && (
            <Section title="🧭 Decision & Context">
              <div className="row g-3">
                {ds.final && <KeyVal label="Final Decision" value={ds.final} />}
                {(typeof ds.confidence === 'number' || Array.isArray(ds.confidence_ci)) && (
                  <KeyVal
                    label="Confidence"
                    value={
                      <>
                        {typeof ds.confidence === 'number' ? fmtPct(ds.confidence) : '—'}
                        {Array.isArray(ds.confidence_ci) && ds.confidence_ci.length === 2 ? (
                          <>
                            {' '}
                            <span style={{ color: '#9aa4b2' }}>(CI </span>
                            {fmtPct(ds.confidence_ci[0])}
                            <span style={{ color: '#9aa4b2' }}>–</span>
                            {fmtPct(ds.confidence_ci[1])}
                            <span style={{ color: '#9aa4b2' }}>)</span>
                          </>
                        ) : null}
                      </>
                    }
                  />
                )}

                {Array.isArray(agentStory?.decision?.confidence_ci) &&
                  agentStory.decision.confidence_ci.length === 2 && (
                    <small className="d-block text-muted mt-1">
                      Confidence interval: {agentStory.decision.confidence_ci[0].toFixed(2)} –{' '}
                      {agentStory.decision.confidence_ci[1].toFixed(2)}
                    </small>
                  )}

                {Array.isArray(agentStory?.decision?.rationale_lines) &&
                  agentStory.decision.rationale_lines.length > 0 && (
                    <ul className="small mt-2 mb-0">
                      {agentStory.decision.rationale_lines.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  )}

                {(ds.new_target !== undefined || ds.new_stop_loss !== undefined) && (
                  <KeyVal
                    label="Adjustments"
                    value={
                      <>
                        TP: {ds.new_target != null ? ds.new_target : '—'} <span style={{ color: '#9aa4b2' }}>•</span>{' '}
                        SL: {ds.new_stop_loss != null ? ds.new_stop_loss : '—'}
                      </>
                    }
                  />
                )}
                {(dc.symbol ||
                  dc.side ||
                  dc.price_at_decision !== undefined ||
                  dc.pnl_at_decision !== undefined ||
                  dc.timestamp) && (
                  <div className="col-12">
                    <div className="rounded p-3" style={{ backgroundColor: '#12151d', border: '1px solid #232a3a' }}>
                      <div className="row g-3">
                        {dc.symbol && <KeyVal label="Symbol" value={dc.symbol} />}
                        {dc.side && <KeyVal label="Side" value={dc.side} />}
                        {dc.price_at_decision !== undefined && (
                          <KeyVal label="Price at Decision" value={`$${fmtNum(dc.price_at_decision, 2)}`} />
                        )}
                        {dc.pnl_at_decision !== undefined && (
                          <KeyVal label="P&L at Decision" value={fmtNum(dc.pnl_at_decision, 2)} />
                        )}
                        {dc.timestamp && <KeyVal label="Timestamp" value={fmtTime(dc.timestamp)} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Reasoning */}
          <Section title="💭 Reasoning">
            <div
              className="p-3 rounded"
              style={{ backgroundColor: '#12151d', border: '1px solid #232a3a', whiteSpace: 'pre-wrap', color: '#e0e0e0' }}
            >
              {Array.isArray(ds.rationale_lines) && ds.rationale_lines.length > 0
                ? ds.rationale_lines.join('\n')
                : legacyReasoning || 'No reasoning provided'}
            </div>
            {narrative && (
              <div className="mt-2">
                <small style={{ color: '#9aa4b2' }}>Narrative</small>
                <div
                  className="p-3 rounded"
                  style={{ backgroundColor: '#12151d', border: '1px solid #232a3a', whiteSpace: 'pre-wrap', color: '#cfd6e4' }}
                >
                  {narrative}
                </div>
              </div>
            )}
          </Section>

          {/* Pattern influence (top-level) */}
          {patternInfluence && (
            <Section title="🪄 Pattern Influence">
              <div className="p-2 rounded" style={{ background: '#12151d', border: '1px solid #232a3a', color: '#cfd6e4', whiteSpace: 'pre-wrap' }}>
                {patternInfluence}
              </div>
            </Section>
          )}

          {/* Pattern Tag Analysis */}
          {(ptag?.pattern ||
            (ptag?.tags && ptag.tags.length) ||
            ptag?.direction ||
            ptag?.vs_history ||
            ptag?.action_map_rule ||
            typeof ptag?.confidence === 'number') && (
            <Section title="🏷️ Pattern Tag Analysis">
              {ptag.pattern && <KeyVal label="Pattern" value={ptag.pattern} />}
              {ptag.direction && <KeyVal label="Direction" value={ptag.direction} />}
              {typeof ptag.confidence === 'number' && <KeyVal label="Pattern Confidence" value={fmtPct(ptag.confidence)} />}
              {ptag.vs_history && <KeyVal label="Vs History" value={ptag.vs_history} />}
              {ptag.action_map_rule && <KeyVal label="Rule" value={ptag.action_map_rule} />}
              {!!(ptag.tags && ptag.tags.length) && (
                <div className="mt-2">
                  <small className="d-block" style={{ color: '#888' }}>Tags</small>
                  <div style={{ color: '#e0e0e0' }}>{ptag.tags.join(', ')}</div>
                </div>
              )}
            </Section>
          )}

          {/* Chart LLM Analysis (captured from agent_story.chart_analysis.llm_full_response) */}
          {chartLLM && (
            <Section title="📊 Chart LLM Analysis">
              <ChartLLMAnalysis data={chartLLM} />
            </Section>
          )}

          {/* Performance (Similar Cases) */}
          {(perf &&
            (perf.total_similar_cases ||
              perf.success_rate !== undefined ||
              perf.similarity_score !== undefined)) && (
            <Section title="📈 Performance (Similar Cases)">
              <div className="row g-3">
                {perf.total_similar_cases !== undefined && <KeyVal label="Cases" value={perf.total_similar_cases} />}
                {perf.success_rate !== undefined && <KeyVal label="Success" value={fmtPct(perf.success_rate)} />}
                {perf.target_hit_rate !== undefined && <KeyVal label="Target Hit" value={fmtPct(perf.target_hit_rate)} />}
                {perf.stop_hit_rate !== undefined && <KeyVal label="Stop Hit" value={fmtPct(perf.stop_hit_rate)} />}
                {perf.avg_pnl !== undefined && <KeyVal label="Avg P&L" value={fmtNum(perf.avg_pnl, 3)} />}
                {perf.expected_value !== undefined && <KeyVal label="Expected Value" value={fmtNum(perf.expected_value, 3)} />}
                {perf.similarity_score !== undefined && <KeyVal label="Similarity" value={fmtPct(perf.similarity_score)} />}
                {perf.confidence_level && <KeyVal label="Confidence Level" value={perf.confidence_level} />}
              </div>
              {(perf.supports_decision !== undefined || perf.used_weight) && (
                <div className="mt-2">
                  <small className="d-block" style={{ color: '#9aa4b2' }}>
                    Supports Decision:{' '}
                    <strong style={{ color: '#e0e0e0' }}>{perf.supports_decision ? 'Yes' : 'No'}</strong>
                    {perf.used_weight ? (
                      <>
                        {' '}
                        • Weight: <strong style={{ color: '#e0e0e0' }}>{perf.used_weight}</strong>
                      </>
                    ) : null}
                  </small>
                </div>
              )}
            </Section>
          )}

          {/* Memory Summary */}
          {(msum?.found_cases ||
            msum?.recent_cases ||
            msum?.historical_cases ||
            msum?.top_similarity ||
            msum?.note) && (
            <Section title="🧠 Memory Summary">
              <div className="row g-3">
                {msum.found_cases !== undefined && <KeyVal label="Found" value={msum.found_cases} />}
                {msum.recent_cases !== undefined && <KeyVal label="Recent" value={msum.recent_cases} />}
                {msum.historical_cases !== undefined && <KeyVal label="Historical" value={msum.historical_cases} />}
                {msum.top_similarity !== undefined && <KeyVal label="Top Similarity" value={fmtPct(msum.top_similarity)} />}
              </div>
              {msum.note && (
                <div className="mt-2" style={{ color: '#cfd6e4' }}>
                  <small className="d-block" style={{ color: '#9aa4b2' }}>Note</small>
                  {msum.note}
                </div>
              )}
            </Section>
          )}

          {/* Thinking Snapshots */}
          {(think?.initial_thinking || think?.combined_thinking || think?.final_thinking) && (
            <Section title="🧩 Thinking Snapshots">
              {think.initial_thinking && (
                <div className="mb-3">
                  <small className="d-block mb-2" style={{ color: '#9aa4b2', fontWeight: 'bold' }}>
                    🔹 Initial Thinking
                  </small>
                  <div className="p-3 rounded" style={{ backgroundColor: '#12151d', border: '1px solid #232a3a' }}>
                    <div className="mb-2">
                      <small className="d-block" style={{ color: '#888' }}>Scope & Decision</small>
                      <div style={{ color: '#e0e0e0' }}>
                        <Badge bg="secondary" className="me-2">{think.initial_thinking.scope || 'unknown'}</Badge>
                        <Badge bg="info">{think.initial_thinking.provisional_decision || '—'}</Badge>
                        {typeof think.initial_thinking.confidence === 'number' && (
                          <Badge bg="light" text="dark" className="ms-2">
                            Conf: {think.initial_thinking.confidence.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {think.initial_thinking.initial_reasoning && (
                      <div className="mt-2">
                        <small className="d-block mb-1" style={{ color: '#888' }}>Reasoning</small>
                        <div
                          className="p-2 rounded"
                          style={{ backgroundColor: '#0f131b', border: '1px solid #1e2535', whiteSpace: 'pre-wrap', color: '#cfd6e4' }}
                        >
                          {think.initial_thinking.initial_reasoning}
                        </div>
                      </div>
                    )}

                    {think.initial_thinking.rule_votes && (
                      <details className="mt-2">
                        <summary style={{ cursor: 'pointer', color: '#9aa4b2' }}>Rule votes</summary>
                        <div className="mt-2">
                          <JsonBlock data={think.initial_thinking.rule_votes} />
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}

              {think.combined_thinking && (
                <div className="mb-3">
                  <small className="d-block mb-2" style={{ color: '#9aa4b2', fontWeight: 'bold' }}>
                    🔄 Combined Thinking
                  </small>
                  <div className="p-3 rounded" style={{ backgroundColor: '#12151d', border: '1px solid #232a3a' }}>
                    {think.combined_thinking.consistency_recommendation && (
                      <div className="mb-2">
                        <small className="d-block" style={{ color: '#888' }}>Consistency Check</small>
                        <Badge
                          bg={
                            think.combined_thinking.consistency_recommendation === 'CONFIRM'
                              ? 'success'
                              : think.combined_thinking.consistency_recommendation === 'OVERRIDE'
                              ? 'warning'
                              : 'secondary'
                          }
                        >
                          {think.combined_thinking.consistency_recommendation}
                        </Badge>
                      </div>
                    )}

                    {think.combined_thinking.consistency_reasoning && (
                      <div className="mt-2">
                        <small className="d-block mb-1" style={{ color: '#888' }}>Integrated Analysis</small>
                        <div
                          className="p-2 rounded"
                          style={{ backgroundColor: '#0f131b', border: '1px solid #1e2535', whiteSpace: 'pre-wrap', color: '#cfd6e4', fontSize: '0.85rem' }}
                        >
                          {think.combined_thinking.consistency_reasoning}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {think.final_thinking && (
                <div className="mb-2">
                  <small className="d-block mb-2" style={{ color: '#9aa4b2', fontWeight: 'bold' }}>✅ Final Thinking</small>
                  <div className="p-3 rounded" style={{ backgroundColor: '#12151d', border: '1px solid #232a3a' }}>
                    <div className="mb-2">
                      <small className="d-block" style={{ color: '#888' }}>Decision & Confidence</small>
                      <div>
                        <Badge
                          bg={
                            think.final_thinking.decision === 'HOLD'
                              ? 'primary'
                              : think.final_thinking.decision === 'CLOSE'
                              ? 'danger'
                              : think.final_thinking.decision === 'ADJUST'
                              ? 'warning'
                              : 'secondary'
                          }
                          className="me-2"
                        >
                          {think.final_thinking.decision || '—'}
                        </Badge>
                        <Badge bg="light" text="dark">{think.final_thinking.confidence || '—'}</Badge>
                      </div>
                    </div>

                    {think.final_thinking.final_reasoning && (
                      <div className="mt-2">
                        <small className="d-block mb-1" style={{ color: '#888' }}>Final Reasoning</small>
                        <div
                          className="p-2 rounded"
                          style={{ backgroundColor: '#0f131b', border: '1px solid #1e2535', whiteSpace: 'pre-wrap', color: '#cfd6e4' }}
                        >
                          {think.final_thinking.final_reasoning}
                        </div>
                      </div>
                    )}

                    {think.final_thinking.trigger_plan && (
                      <div className="mt-3 pt-2" style={{ borderTop: '1px solid #232a3a' }}>
                        <small className="d-block mb-2" style={{ color: '#9aa4b2' }}>📆 Trigger Plan</small>
                        <div className="d-flex flex-wrap gap-2">
                          <small className="d-flex align-items-center">
                            <span style={{ color: '#888' }}>Next check:</span>
                            <Badge bg="dark" className="ms-1">{think.final_thinking.trigger_plan.next_check || '—'}</Badge>
                          </small>
                          {think.final_thinking.trigger_plan.level_trigger && (
                            <small className="d-flex align-items-center">
                              <span style={{ color: '#888' }}>Level trigger:</span>
                              <Badge bg="dark" className="ms-1">{think.final_thinking.trigger_plan.level_trigger}</Badge>
                            </small>
                          )}
                          {typeof think.final_thinking.trigger_plan.false_break_ack === 'boolean' && (
                            <small className="d-flex align-items-center">
                              <span style={{ color: '#888' }}>False-break aware:</span>
                              <Badge bg={think.final_thinking.trigger_plan.false_break_ack ? 'success' : 'secondary'} className="ms-1">
                                {think.final_thinking.trigger_plan.false_break_ack ? 'Yes' : 'No'}
                              </Badge>
                            </small>
                          )}
                        </div>
                        {think.final_thinking.trigger_plan.note && (
                          <div className="mt-2">
                            <small className="text-muted">{think.final_thinking.trigger_plan.note}</small>
                          </div>
                        )}
                      </div>
                    )}

                    {(think.final_thinking.constraints_applied || think.final_thinking.audit_tags) && (
                      <details className="mt-2">
                        <summary style={{ cursor: 'pointer', color: '#9aa4b2' }}>Constraints & audit</summary>
                        <div className="mt-2">
                          {think.final_thinking.constraints_applied && (
                            <div className="mb-2">
                              <small className="d-block mb-1" style={{ color: '#888' }}>Constraints Applied</small>
                              <JsonBlock data={think.final_thinking.constraints_applied} />
                            </div>
                          )}
                          {think.final_thinking.audit_tags &&
                            Array.isArray(think.final_thinking.audit_tags) &&
                            think.final_thinking.audit_tags.length > 0 && (
                              <div>
                                <small className="d-block mb-1" style={{ color: '#888' }}>Audit Tags</small>
                                <div>{think.final_thinking.audit_tags.join(', ')}</div>
                              </div>
                            )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Risk, Contradictions & Ops */}
          {(rctl?.recommendation || contra?.model_vs_pattern || ops?.total_latency_s !== undefined) && (
            <Section title="🛡️ Risk & Ops">
              <div className="row g-3">
                {rctl?.recommendation && <KeyVal label="Risk Recommendation" value={rctl.recommendation} />}
                {rctl?.vol_buffer_basis && <KeyVal label="Vol Basis" value={rctl.vol_buffer_basis} />}
                {contra?.model_vs_pattern && <KeyVal label="Model vs Pattern" value={contra.model_vs_pattern} />}
                {contra?.note && <KeyVal label="Note" value={contra.note} strong={false} />}
              </div>
              {(ops.llm_calls !== undefined || ops.total_latency_s !== undefined || ops.cache_hit_rate !== undefined) && (
                <div className="mt-2">
                  <small className="d-block" style={{ color: '#9aa4b2' }}>
                    Ops: LLM calls <strong style={{ color: '#e0e0e0' }}>{ops.llm_calls ?? '—'}</strong>
                    {' • '}Latency{' '}
                    <strong style={{ color: '#e0e0e0' }}>
                      {typeof ops.total_latency_s === 'number' ? ops.total_latency_s.toFixed(2) : ops.total_latency_s} s
                    </strong>
                    {' • '}Cache <strong style={{ color: '#e0e0e0' }}>{ops.cache_hit_rate !== undefined ? fmtPct(ops.cache_hit_rate) : '—'}</strong>
                  </small>
                </div>
              )}
            </Section>
          )}

          {/* Timeframe protection */}
          {isNonEmptyObject(timeframe) && (
            <Section title="⏱️ Timeframe Protection">
              <JsonBlock data={timeframe} />
            </Section>
          )}

          {/* 🧾 Raw GenAI Message - Collapsible */}
          {genAIraw && (
            <Section title="🧾 Raw GenAI Message">
              <details>
                <summary
                  style={{ cursor: 'pointer', color: '#9aa4b2', padding: '8px', backgroundColor: '#12151d', border: '1px solid #232a3a', borderRadius: '4px' }}
                >
                  <strong>View complete GenAI evaluation response</strong>
                </summary>
                <div className="mt-2">
                  <JsonBlock data={genAIraw} />
                </div>
              </details>
            </Section>
          )}

          {/* Legacy/experimental blocks */}
          <OptionalSection title="⏱️ Exit Risk" value={exitRisk} />
          <OptionalSection title="🚩 Red Flags" value={redFlags} />
          <OptionalSection title="🛤 Journey" value={journey} />
          <OptionalSection title="📶 Signals" value={signals} />

          {/* Unknown keys safety-net */}
          {(() => {
            if (!isNonEmptyObject(agentStory)) return null;
            const known = new Set([
              'schema_version',
              'ids',
              'decision_context',
              'decision',
              'performance',
              'memory_summary',
              'pattern_tag_analysis',
              'thinking_snapshots',
              'risk_controls',
              'contradictions',
              'ops',
              'timeframe_protection',
              'chart_analysis',
            ]);
            const leftovers = Object.fromEntries(Object.entries(agentStory).filter(([k]) => !known.has(k)));
            if (!isNonEmptyObject(leftovers)) return null;
            return (
              <Section title="🧩 Additional Agent Fields">
                <JsonBlock data={leftovers} />
              </Section>
            );
          })()}
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: '#10131a', borderTop: '1px solid #1e2330' }}>
          <Button variant="outline-secondary" onClick={onHide} style={{ color: '#e0e0e0', borderColor: '#3b4352' }}>
            Close
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default EvaluationDetailModal;
